import type { DocPage } from '../types'
import { media } from '../media'

export const autocad: DocPage = {
  id: 'autocad',
  title: 'AutoCAD',
  description:
    'Configure AutoCAD source and target: model modes, entity tables, explode/retain/storage, style import fidelity, EPSG override, and mapped vs scratch write.',
  category: 'Formats',
  sections: [
    { id: 'overview', title: 'Overview' },
    { id: 'connection', title: 'Source Model Connection' },
    { id: 'modes', title: 'Model modes' },
    { id: 'translator', title: 'Translator entity tables' },
    { id: 'blocks', title: 'Explode, retain, storage' },
    { id: 'options', title: 'Common options' },
    { id: 'styles', title: 'Style import fidelity' },
    { id: 'epsg', title: 'EPSG override' },
    { id: 'write', title: 'Mapped vs scratch write' },
    { id: 'attributes', title: 'Common attributes' },
    { id: 'example', title: 'Example: AutoCAD → GeoPackage' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'overview',
      text: 'Overview',
    },
    {
      type: 'paragraph',
      text: 'AutoCAD reads and writes .dwg / .dxf through the local CAD backend started on splash (“Starting CAD backend…”). Choose a Model Mode to control how entities become collections, set style import and EPSG, then map or scratch to the Target.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'connection',
      text: 'Source Model Connection',
    },
    {
      type: 'paragraph',
      text: 'When you choose AutoCAD → New on Source, the Source Model Connection dialog collects model mode, style import options, EPSG, block handling, and the DWG/DXF file.',
    },
    {
      type: 'figure',
      src: media.autocadSourceConnection,
      alt: 'AutoCAD Source Model Connection dialog',
      caption:
        'Figure: Source Model Connection for AutoCAD — Model Mode (Translator / Layer / Entity), Style import (DWG/DXF), EPSG Code, Explode blocks / Retain inserts, and Upload File.',
      size: 'compact',
    },
    {
      type: 'table',
      headers: ['Control', 'Purpose'],
      rows: [
        ['Model Mode', 'Translator (default), Layer, or Entity — how CAD entities become collections.'],
        ['Style import (DWG/DXF)', 'Master switch for extracting styles from the drawing.'],
        ['Resolve BYLAYER / BYBLOCK', 'Resolve ByLayer/ByBlock colors and properties into concrete styles.'],
        ['Import text labels', 'Include Text / MText / related labels in style extraction.'],
        ['Import hatch / fills', 'Include hatch and region fill symbolizers.'],
        ['Import block symbols', 'Import block markers instead of stroke-only fallbacks.'],
        ['Build shared style library', 'Share style definitions across entities when possible.'],
        ['Style fidelity', 'Basic / Standard / High detail for extracted styles.'],
        ['EPSG Code', 'Override / assign the drawing CRS (critical for Map view and transfer).'],
        ['Explode blocks', 'Explode BlockReference entities into geometry collections (Translator).'],
        ['Retain inserts', 'Keep parent insert rows when exploding; choose cad_block or cad_point storage.'],
        ['Upload File', 'Pick the .dwg / .dxf source file and proceed.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'modes',
      text: 'Model modes',
    },
    {
      type: 'table',
      headers: ['Mode', 'How collections are built'],
      rows: [
        [
          'Translator',
          'Fixed collections: cad_area, cad_line, cad_point, cad_text (plus optional explode/block collections).',
        ],
        ['Layer', 'One collection per CAD layer.'],
        [
          'Entity',
          'One collection per entity type; optional Show All Entity Types.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'translator',
      text: 'Translator entity tables',
    },
    {
      type: 'table',
      monoFirst: true,
      headers: ['Collection', 'Entity types (typical)'],
      rows: [
        ['cad_area', 'Region, Hatch, Solid, Solid3d'],
        [
          'cad_line',
          'Line, Polyline, Polyline2d/3d, Circle, Ellipse, Arc, Spline, Face',
        ],
        ['cad_point', 'DBPoint, BlockReference, Insert'],
        ['cad_text', 'Text, MText, DBText'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'blocks',
      text: 'Explode, retain, storage',
    },
    {
      type: 'table',
      headers: ['Option', 'Effect'],
      rows: [
        [
          'Explode blocks',
          'Creates additional collections cad_block_area, cad_block_line, cad_block_text, cad_block_point for exploded geometry.',
        ],
        [
          'Retain inserts',
          'Keeps insert references; may expose _xf_id / _xf_parent_id for lineage.',
        ],
        [
          'Insert storage cad_block / cad_point',
          'Controls whether inserts are stored under cad_block or cad_point collections.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'options',
      text: 'Common options',
    },
    {
      type: 'list',
      items: [
        'Include feature counts in the model tree (via Feature Options / format options).',
        'Extract tapered polylines as polygons when applicable.',
        'Override Existing on target write when replacing drawing content as offered.',
        'File picker for source/target .dwg / .dxf paths.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'styles',
      text: 'Style import fidelity',
    },
    {
      type: 'paragraph',
      text: 'Style import (DWG/DXF) options:',
    },
    {
      type: 'table',
      headers: ['Option', 'Description'],
      rows: [
        ['Resolve BYLAYER / BYBLOCK', 'Resolve entity properties that inherit BYLAYER/BYBLOCK.'],
        ['Import text labels', 'Bring text into the style/label pipeline.'],
        ['Import hatch/fills', 'Include hatch and fill symbology.'],
        ['Import block symbols', 'Include block symbol definitions.'],
        ['Build shared style library', 'Accumulate reusable style library entries when configured.'],
        ['Basic', 'Lowest fidelity / fastest style import.'],
        ['Standard', 'Balanced fidelity (typical default choice).'],
        ['High Fidelity', 'Maximum style detail for High quality imports.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'epsg',
      text: 'EPSG override',
    },
    {
      type: 'paragraph',
      text: 'Set EPSG Code on the source form (override_epsg). Placeholder: Select EPSG code. Search by code or name. CAD map overview and spatial filters require a correct Override EPSG — see Map view & attribute table.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'write',
      text: 'Mapped vs scratch write',
    },
    {
      type: 'table',
      headers: ['Mode', 'Behavior'],
      rows: [
        [
          'Mapped',
          'Write into an existing DWG/DXF model according to collection/attribute mappings.',
        ],
        [
          'Scratch',
          'Create a new drawing file from the source; naming hints follow cad_line, cad_area, or by layer/entity depending on Model Mode.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'attributes',
      text: 'Common attributes',
    },
    {
      type: 'paragraph',
      text: 'Typical CAD attributes include aci, rgb, linetype, layer_name, entity_name, dbhandle, and geometry attributes area / line / point / text depending on the collection.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'example',
      text: 'Example: AutoCAD → GeoPackage',
    },
    {
      type: 'steps',
      items: [
        'Source AutoCAD: pick DWG/DXF, choose Translator mode, set style import fidelity and EPSG Code, apply filters if needed.',
        'Target Geopackage: choose path, Override Existing if desired, configure style export (QML/SLD/layer_styles).',
        'Connect cad_line / cad_area / … to GPKG layers (or use Auto Map).',
        'Transfer, then verify geometry and optional style sidecars.',
      ],
    },
  ],
}
